import { useEffect, useState } from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { default as Grid } from '@mui/material/Grid2';
import LinearProgress from '@mui/material/LinearProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DownloadIcon from '@mui/icons-material/Download';

import { enqueueSnackbar } from 'notistack';

import { isValidEmail } from 'utils-ts/email-helpers';
import useInvoicePdf, { type InvoicePdfData } from 'hooks-ts/useInvoicePdf';
import { sendInvoicePdfEmail } from 'utils-ts/sales-and-invoicing';
import handleError from 'utils-ts/handle-error';

// @ts-expect-error not typescript yet
import { TipTapEditor } from 'ui-component/TipTapEditor';
import { type Invoice } from '@a1/shared';

interface InvoicePdfDialogProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  invoice?: Invoice | null;
}

const defaultHtml = import.meta.env.VITE_INVOICE_EMAIL_MESSAGE_DEFAULT || 'Here is a copy of your invoice.';

const SendInvoicePdfDialog = ({ open, setOpen, invoice }: InvoicePdfDialogProps) => {
  const { getInvoicePdfData, generateInvoicePdf } = useInvoicePdf();
  const invoiceId = invoice?.id;

  const [subject, setSubject] = useState<string>('');
  const [emails, setEmails] = useState<string[]>([]);
  const [emailInputValue, setEmailInputValue] = useState('');
  const [html, setHtml] = useState<string>(defaultHtml);
  const [sending, setSending] = useState<boolean>(false);

  const [invoicePdfData, setInvoicePdfData] = useState<InvoicePdfData | null>(null);
  const [loadingInvoicePdfData, setLoadingInvoicePdfData] = useState(true);

  const bankDetails = invoicePdfData?.bankDetails;

  useEffect(() => {
    const getPdfData = async () => {
      try {
        setLoadingInvoicePdfData(true);
        const invoicePdfDetails = await getInvoicePdfData({ invoiceId });
        if (invoicePdfDetails) {
          setInvoicePdfData(invoicePdfDetails);
        }
      } catch (error) {
        handleError(error);
      } finally {
        setLoadingInvoicePdfData(false);
      }
    };

    if (open && invoiceId) {
      getPdfData();
    } else {
      setLoadingInvoicePdfData(false);
    }
  }, [setInvoicePdfData, getInvoicePdfData, invoiceId, open]);

  const disableSend = emails.length < 1 || !html.trim() || !invoice || !invoicePdfData?.blob;

  const handleClose = () => {
    setOpen(false);
    setEmails([]);
    setSubject('');
    setHtml(defaultHtml);
  };

  useEffect(() => {
    if (invoice?.invoiceNumber) {
      setSubject(`${invoice.invoiceNumber}${bankDetails?.companyName ? ` from ${bankDetails.companyName}` : ''}`);
    }
  }, [bankDetails?.companyName, invoice]);

  const handleSend = async () => {
    if (!invoice) {
      return;
    }
    if (!invoicePdfData?.blob) {
      throw new Error('Invoice PDF blob is not available.');
    }

    setSending(true);
    try {
      await sendInvoicePdfEmail({
        emails,
        subject,
        html,
        pdfBlob: invoicePdfData.blob,
        invoiceNumber: invoice.invoiceNumber ?? undefined,
        onSuccess: (msg) =>
          enqueueSnackbar({
            variant: 'success',
            message: msg,
            anchorOrigin: { vertical: 'top', horizontal: 'right' }
          }),
        onError: (msg) =>
          enqueueSnackbar({
            variant: 'error',
            message: msg,
            anchorOrigin: { vertical: 'top', horizontal: 'right' }
          })
      });
    } finally {
      setSending(false);
    }
  };

  const handleEmailInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if ((event.key === 'Enter' || event.key === 'Tab') && emailInputValue) {
      event.preventDefault();
      const trimmed = emailInputValue.trim();
      if (trimmed) {
        setEmails([...emails, trimmed]);
        setEmailInputValue('');
      }
    }
  };

  return (
    <>
      <Dialog open={open} fullWidth maxWidth="sm">
        <DialogTitle>Send PDF {invoice?.invoiceNumber && `for ${invoice.invoiceNumber}`}</DialogTitle>

        {loadingInvoicePdfData ? (
          <LinearProgress />
        ) : (
          <>
            <DialogContent>
              {invoice ? (
                <Grid container pt={2} spacing={2}>
                  {/* send to */}
                  <Grid size={12}>
                    <Autocomplete
                      multiple
                      id="emails"
                      options={[]}
                      freeSolo
                      value={emails}
                      inputValue={emailInputValue}
                      onInputChange={(_, value) => {
                        setEmailInputValue(value);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="To - press 'Enter' or 'Tab' to add emails"
                          required
                          onKeyDown={handleEmailInputKeyDown}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option}
                            label={option}
                            // Add visual cue if a chip itself is invalid
                            color={isValidEmail(option) ? 'primary' : 'error'}
                          />
                        ))
                      }
                      onChange={(_, value) => {
                        setEmails(value);
                      }}
                      disabled={sending}
                    />
                  </Grid>

                  {/* subject */}
                  <Grid size={12}>
                    <TextField
                      value={subject}
                      onChange={(e) => {
                        setSubject(e.target.value);
                      }}
                      fullWidth
                      label="Subject"
                      disabled={sending}
                    />
                  </Grid>

                  <Grid size={12}>
                    <TipTapEditor data={html} onChange={setHtml} disabled={sending} />
                  </Grid>

                  {invoicePdfData?.url && (
                    <Grid size={12}>
                      <Button
                        startIcon={<DownloadIcon />}
                        fullWidth
                        onClick={() => generateInvoicePdf({ invoiceId, filename: `${invoice.invoiceNumber ?? 'invoice'}.pdf` })}
                      >
                        {invoice.invoiceNumber ?? 'Invoice'}
                      </Button>
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Typography>Invoice could not be loaded.</Typography>
              )}
            </DialogContent>

            <DialogActions>
              <Button color="error" variant="text" onClick={handleClose} disabled={sending}>
                Close
              </Button>
              <Button loading={sending} disabled={disableSend} onClick={handleSend}>
                Send
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default SendInvoicePdfDialog;
